import { Component, ContentChild, ContentChildren, ElementRef, Input, QueryList, ViewChild } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { map, debounceTime } from 'rxjs/operators'

@Component({
    templateUrl: 'll-tabs.component.html',
    selector: 'll-tabs',
    styleUrls: ['./ll-tabs.component.scss']
})
export class LlTabsComponent {
  @Input() private  jump = 50;
  @ViewChild('tabWrapper') tabWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('tabContainer') tabContainer!: ElementRef<HTMLDivElement>;
  @ContentChild('leftButton') set leftButton(value: ElementRef<HTMLButtonElement>) {
    this._leftButton = value;
    this._startButtonTriggerSubscription(value?.nativeElement, 'left');
    setTimeout(() => this._updateView());
  }
  private _leftButton!: ElementRef<HTMLButtonElement>
  @ContentChild('rightButton') set rightButton(value: ElementRef<HTMLButtonElement>) {
    this._rightButton = value;
    this._startButtonTriggerSubscription(value?.nativeElement, 'right');
    setTimeout(() => this._updateView());
  }

  @ContentChildren('tabItem') set tabItems (value: QueryList<ElementRef<HTMLElement>>) {
    this._tabItems = value;
    this._updateSelectedTabIndex();
    this._updateStyleOnElements();
    setTimeout(() => this._updateView());
    this._refreshContentChildSubscriptions();
    this._scrollToItem(this._selectedTabIndex);

  }

  private _tabItems!: QueryList<ElementRef<HTMLElement>>

  private _rightButton!: ElementRef<HTMLButtonElement>;

  private _tabSubscriptionManager!: Subscription;
  private _subscriptionManager = new Subscription();

  private _divLeft: HTMLDivElement | undefined;
  private _divRight: HTMLDivElement | undefined;

  @Input() set selectedTabIndex(value: number) {
    this._selectedTabIndex = value;
    if (this._tabItems) this._updateActiveElemet(this._tabItems.get(value)?.nativeElement, value)
  }

  private _selectedTabIndex = 0;

  private readonly ACTIVE_TAB_CLASS = 'active-tab'


  ngAfterViewInit(): void {
    this._startWheelSubscription();
    this._startScrollSubscription();
    setTimeout(() => this._updateView());
    setTimeout(() => this._updateStyleOnElements());
    setTimeout(() => this._updateActiveElemet(this._tabItems.get(this._selectedTabIndex)?.nativeElement, this._selectedTabIndex), 100);

    this._subscriptionManager.add(fromEvent(window, 'resize').pipe(debounceTime(100)).subscribe(resizeEvent => this._updateView()));

  }

  ngOnDestroy(): void {
    if (!this._tabSubscriptionManager.closed) this._tabSubscriptionManager.unsubscribe();
    this._subscriptionManager.unsubscribe();
  }


  private _updateButtonsStyle(isVisible: boolean) {
    if (!isVisible) {
      if (this._leftButton)
        this._leftButton.nativeElement.style.display = 'none';
      if (this._rightButton)
        this._rightButton.nativeElement.style.display = 'none';
      return;
    }
    if (this._leftButton) {
      this._leftButton.nativeElement.style.display = 'unset';
      this._leftButton.nativeElement.style.position = 'absolute';
      this._leftButton.nativeElement.style.left = '0';
    }
    if (this._rightButton) {
      this._rightButton.nativeElement.style.display = 'unset';
      this._rightButton.nativeElement.style.position = 'absolute';
      this._rightButton.nativeElement.style.right = '0';
    }
  }

  private _startButtonTriggerSubscription(element: HTMLElement, direction: 'left' | 'right') {
    if (!element) return
      this._subscriptionManager.add(fromEvent(element, 'click').subscribe(() =>  this.scrollContent(direction)));
  }

  private _startWheelSubscription() {
    this._subscriptionManager.add(fromEvent(this.tabContainer.nativeElement, 'wheel').pipe(
      map(v => {
        const event = v as unknown as WheelEvent
        const numDir = event.deltaX || event.deltaY;
        return numDir
      })
    ).subscribe((scrollDelta) => {
      this.tabContainer.nativeElement.scrollTo({left: this.tabContainer.nativeElement.scrollLeft + scrollDelta});
    }))
  }

  private _startScrollSubscription() {
    this._subscriptionManager.add(fromEvent(this.tabContainer.nativeElement, 'scroll').pipe(
      debounceTime(100),
      map(v => (v.target as HTMLDivElement).scrollLeft)
    ).subscribe(v => {
      if (v === 0 && this._leftButton) 
        this._leftButton.nativeElement.classList.add('ll-scroll-button-disabled');
      else if (this._leftButton)
        this._leftButton.nativeElement.classList.remove('ll-scroll-button-disabled');
      if (this._rightButton && this.tabWrapper.nativeElement.scrollWidth - this.tabContainer.nativeElement.getBoundingClientRect().width <= v)
        this._rightButton.nativeElement.classList.add('ll-scroll-button-disabled')
      else
        this._rightButton.nativeElement.classList.remove('ll-scroll-button-disabled');
    }))
  }

  private _updateSelectedTabIndex() {
    if (!this._tabItems) return;
    let newIndex = 0;
    for (const tabRef of this._tabItems) {
      if (tabRef.nativeElement.classList.contains(this.ACTIVE_TAB_CLASS)) {
        this._selectedTabIndex = newIndex;
        break;
      }
      ++newIndex;
    }
  }

  private _refreshContentChildSubscriptions() {
    if (!this._tabSubscriptionManager?.closed) this._tabSubscriptionManager?.unsubscribe();
    this._tabSubscriptionManager = new Subscription();
    this._tabItems.forEach((elementRef, index) => {
      const element = elementRef.nativeElement;
      this._tabSubscriptionManager.add(fromEvent(element, 'click').subscribe(() => this._updateActiveElemet(element, index)))
    })
  }

  private _updateStyleOnElements() {
    if (!this._tabItems) return;
    this._tabItems.forEach(item => item.nativeElement.style.flexShrink = '0')
  }

  private _updateActiveElemet(element: HTMLElement | undefined, index: number) {
    this._tabItems.forEach(t => t.nativeElement.classList.remove(this.ACTIVE_TAB_CLASS));
    if (!element) {
      this._scrollToItem(0);
      return;
    }
    this._scrollToItem(index);
    element.classList.add(this.ACTIVE_TAB_CLASS);
  }

  private _updateView() {
    const containerWidth = this.tabContainer?.nativeElement.clientWidth;
    const contentWidth = this.tabContainer?.nativeElement.scrollWidth;
    if (containerWidth === undefined || contentWidth === undefined) return;
    if (containerWidth >= contentWidth) {
      this._updateButtonsStyle(false)
      if (this._divLeft) {
        this.tabWrapper.nativeElement.removeChild(this._divLeft);
        this._divLeft = undefined;
      }
      if (this._divRight) {
        this.tabWrapper.nativeElement.removeChild(this._divRight);
        this._divRight = undefined;
      }
      return;
    }
    this._updateButtonsStyle(true)
    let leftPadding = 0;
    let rightPadding = 0;
    if (this._leftButton) leftPadding = this._leftButton.nativeElement.getBoundingClientRect().width;
    if (this._rightButton) rightPadding = this._rightButton.nativeElement.getBoundingClientRect().width;
    if (!this._divLeft) {
      this._divLeft = document.createElement('div');
      this.tabWrapper.nativeElement.insertBefore(this._divLeft, this.tabWrapper.nativeElement.firstChild as HTMLElement);
    }
    if (!this._divRight) {
      this._divRight = document.createElement('div');
      this.tabWrapper.nativeElement.appendChild(this._divRight);
    }
    this._divLeft.setAttribute('style', `height: 100%; width: ${leftPadding}px`);
    this._divRight.setAttribute('style', `height: 100%; width: ${rightPadding}px`);

  }

  public scrollContent(direction: 'left' | 'right') {
    let newPosition = this.tabContainer.nativeElement.scrollLeft;
    if (direction === 'right') newPosition += this.jump;
    if (direction === 'left' ) newPosition -=this.jump;
    this.tabContainer.nativeElement.scrollTo({left: newPosition, behavior: 'smooth'});
  }

  private _scrollToItem(index: number) {
    const element = this._tabItems.get(index)?.nativeElement;
    if (!element) return;
    const {x: elementX, width: elementWidth} = element.getBoundingClientRect();
    const tabContainer = this.tabContainer?.nativeElement;
    const tabWrapper = this.tabWrapper?.nativeElement;
    if (!tabContainer || !tabWrapper) return;
    let isElementOutOfBounds = false;
    const tabWrapperPaddingLeft = this._divLeft?.getBoundingClientRect().width || 0;
    const tabWrapperPaddingRight = this._divRight?.getBoundingClientRect().width || 0;
    // const wrapperViewWidth = tabWrapper.getBoundingClientRect().width - (tabWrapperPaddingLeft + tabWrapperPaddingRight);

    // element out to the left;
    if (elementX - tabContainer.getBoundingClientRect().x - tabWrapperPaddingLeft < 0) isElementOutOfBounds = true;
    // element out to the right;
    if (elementX + elementWidth > tabContainer.getBoundingClientRect().x + tabContainer.getBoundingClientRect().width - tabWrapperPaddingRight) isElementOutOfBounds = true;

    if (!isElementOutOfBounds) return;

    tabContainer.scrollTo({left: elementX - tabContainer.getBoundingClientRect().x - tabWrapperPaddingLeft + tabContainer.scrollLeft, behavior: 'smooth'});

  }
}